import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faNewspaper,
  faFolder,
  faCalendarDays,
  faUser,
  faArrowLeft,
  faFire,
  faBookmark,
  faEye,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ContactBanner from '../components/ContactBanner';
import './SinglePost.css';

function SinglePost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      // Find post by slug
      const response = await apiService.getPosts({ slug });
      const posts = response.data.data || response.data;
      
      if (posts && posts.length > 0) {
        const foundPost = posts[0];
        setPost(foundPost);
        
        // Increment view count
        try {
          await apiService.incrementPostViews(foundPost.id);
        } catch (viewError) {
          console.error('Error incrementing views:', viewError);
          // Don't fail if view increment fails
        }
      } else {
        setError('Post not found');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading post:', error);
      setError('Failed to load post');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatViews = (views) => {
    if (views >= 1000000) {
      return (views / 1000000).toFixed(1) + 'M';
    } else if (views >= 1000) {
      return (views / 1000).toFixed(1) + 'K';
    }
    return views || 0;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="single-post-loading">
        <div className="single-post-spinner"></div>
        <p>Loading post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="single-post-error">
        <h1>404</h1>
        <h2>Post Not Found</h2>
        <p>{error || 'The post you are looking for does not exist.'}</p>
        <button onClick={() => navigate('/')} className="single-post-btn">
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - Bonlineco Blog</title>
        <meta name="description" content={stripHtml(post.excerpt || post.content).substring(0, 160)} />
        <meta name="keywords" content={`${post.category?.name || 'blog'}, ${post.title}, bonlineco`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={stripHtml(post.excerpt || post.content).substring(0, 200)} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://blog.bonlineco.com/post/${post.slug}`} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={stripHtml(post.excerpt || post.content).substring(0, 200)} />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
        <meta name="author" content={post.user?.name || 'Bonlineco'} />
        <meta property="article:published_time" content={post.created_at} />
        {post.category && <meta property="article:section" content={post.category.name} />}
        <link rel="canonical" href={`https://blog.bonlineco.com/post/${post.slug}`} />
      </Helmet>

      <div className="single-post">
        {/* Header */}
        <header className="single-post-header">
          <div className="single-post-container">
            <div className="single-post-header-content">
              <div className="single-post-logo">
                <FontAwesomeIcon icon={faNewspaper} className="single-post-logo-icon" />
                <h1>Bonlineco Blog</h1>
              </div>
              
              <nav className="single-post-nav">
                <a href="/" className="single-post-nav-link">
                  <FontAwesomeIcon icon={faFire} />
                  Home
                </a>
                <a href="https://bonlineco.com" className="single-post-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faNewspaper} />
                  Services
                </a>
                <a href="https://store.bonlineco.com" className="single-post-nav-link" target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faBookmark} />
                  Store
                </a>
                <a href="/contact" className="single-post-nav-link">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Contact
                </a>
                {isAuthenticated && (
                  <a href="/admin_v3jq5z/dashboard" className="single-post-nav-link single-post-nav-link-admin">
                    <FontAwesomeIcon icon={faBookmark} />
                    Admin
                  </a>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="single-post-article">
          <div className="single-post-container-narrow">
            {/* Back Button */}
            <button onClick={() => navigate('/')} className="single-post-back">
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Blog
            </button>

            {/* Category */}
            {post.category && (
              <span className="single-post-category">
                <FontAwesomeIcon icon={faFolder} />
                {post.category.name}
              </span>
            )}

            {/* Title */}
            <h1 className="single-post-title">{post.title}</h1>

            {/* Meta */}
            <div className="single-post-meta">
              <span className="single-post-meta-item">
                <FontAwesomeIcon icon={faCalendarDays} />
                {formatDate(post.created_at)}
              </span>
              {post.user && (
                <span className="single-post-meta-item">
                  {(post.user.avatar && post.user.avatar.trim() !== '') ? (
                    <img 
                      src={`https://blog.bonlineco.com/storage/${post.user.avatar}`} 
                      alt={post.user.name}
                      className="single-post-author-avatar"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUser} />
                  )}
                  {post.user.name}
                </span>
              )}
              <span className="single-post-meta-item single-post-views">
                <FontAwesomeIcon icon={faEye} />
                <span className="single-post-view-count">{formatViews(post.views)}</span>
                <span className="single-post-view-label">views</span>
              </span>
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="single-post-featured-image">
                <img src={post.featured_image} alt={post.title} />
              </div>
            )}

            {/* Content */}
            <div 
              className="single-post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Contact Banner */}
            <ContactBanner />
          </div>
        </article>

        {/* Footer */}
        <footer className="single-post-footer">
          <div className="single-post-container">
            <div className="single-post-footer-content">
              <div className="single-post-footer-brand">
                <FontAwesomeIcon icon={faNewspaper} className="single-post-footer-icon" />
                <h4>Bonlineco Blog</h4>
                <p>Quality content for curious minds</p>
              </div>
              <div className="single-post-footer-links">
                <div className="single-post-footer-section">
                  <h5>Quick Links</h5>
                  <a href="/">Home</a>
                  <a href="/contact">Contact</a>
                  <a href="https://bonlineco.com" target="_blank" rel="noopener noreferrer">Services</a>
                  <a href="https://store.bonlineco.com" target="_blank" rel="noopener noreferrer">Store</a>
                  {isAuthenticated && (
                    <a href="/admin_v3jq5z/dashboard">Admin Panel</a>
                  )}
                </div>
              </div>
            </div>
            <div className="single-post-footer-bottom">
              <p>&copy; {new Date().getFullYear()} Bonlineco Blog. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default SinglePost;
